/**
 * The finance health contract (IBA-117): `GET /finance/overview/health`.
 *
 * A financial ratio — and the verdict on it — is an ACCOUNTING OUTPUT, not presentation. These used to be
 * derived in the browser from whatever figures happened to be on screen, with the good/warning/critical
 * thresholds living in a widget. Two clients could therefore disagree about whether a church was solvent.
 * They are now computed once, on the server, from the ledger, and shipped whole.
 */

import type { MoneyString } from "./money";

/** The registered ratios. Adding one means: a member here, a server-side definition, and its i18n block. */
export enum FinanceHealthRatioId {
  CurrentRatio = "currentRatio",
  QuickRatio = "quickRatio",
  DebtRatio = "debtRatio",
  OperatingMargin = "operatingMargin",
}

/** The verdict on a ratio. Decided on the server — the client only picks a colour for it. */
export enum FinanceHealthStatus {
  Good = "good",
  Warning = "warning",
  Critical = "critical",
  /** Computed, but there is nothing to assess (e.g. a debt-free church's current ratio) — reads as positive. */
  Neutral = "neutral",
}

export enum FinanceHealthRatioUnit {
  Ratio = "ratio",
  Percent = "percent",
}

/**
 * The ledger figures every ratio draws from — the financial statements for the period.
 *
 * These are MONEY, so they cross the wire as canonical decimal strings at `FinanceHealthResponse.currency`'s
 * scale (IBA-116), exactly as the statements they are read from already do. They used to be de-migrated back
 * into floats on their way out of the API, which is the one conversion this contract exists to prevent.
 *
 * The RATIOS below stay `number` on purpose: a quotient is not money, it has no currency and no minor unit.
 */
export interface FinanceHealthFigures {
  assets: MoneyString;
  liabilities: MoneyString;
  cash: MoneyString;
  income: MoneyString;
  expenses: MoneyString;
  surplus: MoneyString;
}

export interface FinanceHealthRatio {
  id: FinanceHealthRatioId;
  /** False when the scope lacks the inputs this ratio needs — the client shows an unavailable state. */
  available: boolean;
  /** The value, or null when undefined-but-fine (e.g. no liabilities => Neutral). Never NaN, never Infinity. */
  value: number | null;
  /** The same ratio over the previous equal-length period, or null when it cannot be computed. */
  previous: number | null;
  /** value − previous (the variance), or null when there is no comparison. Computed on the server. */
  delta: number | null;
  /** Whether `delta` is a move in the healthy direction, given `lowerIsBetter`. Null when there is no delta. */
  improving: boolean | null;
  status: FinanceHealthStatus;
  unit: FinanceHealthRatioUnit;
  /** Lower is healthier (e.g. debt ratio) — drives the trend arrow's colour. */
  lowerIsBetter: boolean;
}

export interface FinanceHealthPeriod {
  dateFrom: string | null;
  dateTo: string | null;
}

export interface FinanceHealthResponse {
  period: FinanceHealthPeriod;
  /** The equal-length window immediately before `period`, or null when the period is open-ended. */
  previousPeriod: FinanceHealthPeriod | null;
  /** The scope's accounting currency — the scale every figure above is denominated at. `"1500"` is not an amount without it. */
  currency: string;
  /**
   * The statement figures the ratios were computed from — NULL when the caller may see the verdict but not
   * the money (ADR-0024 §5). A ratio endpoint that ships its own operands has disclosed them, whatever the
   * ratios beside them say.
   */
  figures: FinanceHealthFigures | null;
  previousFigures: FinanceHealthFigures | null;
  ratios: FinanceHealthRatio[];
}
